/**
 * @bos/portal3 — THE LIFECYCLE PICTURE'S LAYOUT (phase 2): a pure, React-free module that
 * turns one derived state machine into placed pills, routed moves and labels.
 *
 * WHY NOT `erLayout.ts`. The two pictures share an approach — layer the graph, order each
 * column by its neighbours' average position, measure every box, and route anything that is
 * not a short forward hop through a bus lane below the body — but they do NOT share a graph.
 * An entity box is a table of fields with a row an edge can anchor to; a state is a PILL with
 * nothing inside it. A model's FK graph is acyclic by the chain's own laws; a lifecycle is
 * cyclic on purpose (Ready → Occupied → NeedsCleaning → Ready is the whole point of Room), so
 * the ER's longest-path layering would not even terminate the same way here. Two small honest
 * modules beat one module with a mode flag.
 *
 * THE ALGORITHM (worked against Hotel Lovor's real machines before it was written here):
 *   1. LAYERING — breadth-first distance from the INITIAL state. Cycle-safe by construction
 *      (a state is layered once), and it puts the columns in the order a person tells the
 *      story: Pending, then what Pending can become, then what those can become. A machine
 *      with no initial seeds from the states nothing moves into; a state unreachable from any
 *      seed is layered after everything reachable rather than dropped — the picture shows what
 *      the bytes say, including a state the moves cannot get to.
 *   2. ORDERING — barycenter sweeps (4 passes) per column, which is what puts Cancelled beside
 *      Confirmed instead of three rows away from every move that reaches it.
 *   3. COORDINATES — MEASURED pills: the width is the state's own name at an average glyph
 *      width, clamped, so `NeedsCleaning` and `Ready` are each exactly as wide as they need.
 *   4. MOVES —
 *        · a SELF move loops over the top of its own pill (no real machine has one today; the
 *          picture must not break the day one does);
 *        · a move to the NEXT column is one cubic curve across the gap, labelled with its
 *          event on two short stacked lines, staggered so neighbours cannot collide;
 *        · anything else — a move BACK, a move inside one column, a move skipping a column —
 *          routes down into its own BUS LANE below the body, across, and back up. Room's four
 *          back-moves are exactly this case, and it is why no line is ever drawn over a pill
 *          it does not concern. A bus label is a full sentence on its own lane.
 *   5. PARALLEL MOVES keep their own lanes and their own labels. `OutOfService --bringBack-->
 *      Ready` and `OutOfService --bringBack--> NeedsCleaning` are two different facts about the
 *      business and are drawn as two moves; nothing is merged.
 *
 * Everything here is arithmetic over the shaped doc: no fetch, no DOM measurement, no
 * randomness. The same machine always lays out the same way, which is what makes it testable.
 */

// ── what the layout reads ────────────────────────────────────────────────────

/** One state, plus whether it is only in the BASKET so far (never drawn as derived). */
export interface LcStateInput {
  readonly name: string;
  readonly pending: boolean;
}

/** One move, in the shape `LifecycleMachineDoc.transitions` serves — plus the basket flag. */
export interface LcTransitionInput {
  readonly from: string;
  readonly to: string;
  readonly event: string | null;
  readonly guard: string | null;
  readonly enforcesRule: string | null;
  readonly pending: boolean;
}

export interface LcLayoutInput {
  readonly states: readonly LcStateInput[];
  readonly initial: string | null;
  readonly terminal: readonly string[];
  readonly transitions: readonly LcTransitionInput[];
}

// ── what the layout produces ─────────────────────────────────────────────────

export interface LcNode {
  readonly state: string;
  /** Position in the input's `states` — the renderer's key back to the state's own data. */
  readonly index: number;
  readonly column: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** Where the machine starts — drawn with an arrow into it from nowhere. */
  readonly initial: boolean;
  /** The end of the road — drawn as a double ring. */
  readonly terminal: boolean;
  /** In the basket, not on disk. */
  readonly pending: boolean;
}

export interface LcEdge {
  /** Position in the input's `transitions` — the identity a click reports back. */
  readonly index: number;
  readonly from: string;
  readonly to: string;
  readonly event: string | null;
  readonly guard: string | null;
  readonly enforcesRule: string | null;
  readonly pending: boolean;
  /** A move from a state to itself: drawn as a loop over its own pill. */
  readonly self: boolean;
  /** −1 = a curve in the column gap (or a self loop); ≥0 = routed through that bus lane. */
  readonly lane: number;
  readonly d: string;
  /** Where this move's own label sits (line 1; a second line stacks 12px below). */
  readonly labelX: number;
  readonly labelY: number;
  /** A bus label is a whole sentence on its lane; a gap label is the event alone. */
  readonly bus: boolean;
}

export interface LcLayout {
  readonly nodes: readonly LcNode[];
  readonly edges: readonly LcEdge[];
  readonly width: number;
  readonly height: number;
  /** The bottom of the last pill — where the bus lanes begin. */
  readonly bodyBottom: number;
  readonly busLanes: number;
  readonly columns: number;
}

// ── the constants the renderer draws with (one home, so CSS and geometry agree) ──

/** The height of one state pill. */
export const STATE_H = 32;
/** The gap between columns — the room a move's own label lives in. */
export const LC_COL_GAP = 156;
/** The gap between stacked pills in a column. */
export const LC_ROW_GAP = 24;
/** The canvas margin on every side (the left one also holds the initial arrow). */
export const LC_MARGIN = 30;
/** The height of one bus lane below the body. */
export const LC_BUS_LANE_H = 20;
/** Headroom reserved above EACH pill that has a move looping over it (per pill, not per
 *  body: a looper below the column top must not draw its loop through the pill above). */
export const LC_SELF_LOOP_H = 44;
/** How long the arrow into the initial state is. */
export const LC_INITIAL_ARROW = 20;

/**
 * MEASURED pill width: the state's own name at an average glyph width, clamped 76–230px. SVG
 * text does not reflow, so a fixed pill is exactly what makes `takeOutOfService`'s target and
 * `Ready` look like the same size when they are not.
 */
export function stateWidth(name: string): number {
  return Math.min(230, Math.max(76, Math.ceil(7.4 * name.length + 28)));
}

/** The whole move on one line — what a bus lane's own label says. */
export function moveLabel(t: { from: string; to: string; event: string | null }): string {
  return t.event !== null ? `${t.from} → ${t.to} · ${t.event}` : `${t.from} → ${t.to}`;
}

/** A move with no recorded event has no name to put in a gap — the picture says so plainly. */
export function gapLabel(t: { event: string | null }): string {
  return t.event ?? 'no name recorded';
}

// ── the layout itself ────────────────────────────────────────────────────────

export function layoutLifecycle(machine: LcLayoutInput): LcLayout {
  const states = machine.states;
  const names = states.map((s) => s.name);
  const index = new Map<string, number>();
  names.forEach((n, i) => {
    if (!index.has(n)) index.set(n, i); // a duplicated name resolves to its first pill
  });
  const n = names.length;

  // Forward adjacency over the moves that name two known states.
  const out: number[][] = names.map(() => []);
  const incoming: boolean[] = names.map(() => false);
  for (const t of machine.transitions) {
    const a = index.get(t.from);
    const b = index.get(t.to);
    if (a === undefined || b === undefined || a === b) continue;
    out[a].push(b);
    incoming[b] = true;
  }

  // 1. Breadth-first layering from the initial state. Cycle-safe: a state is layered once.
  const col: number[] = names.map(() => -1);
  const seeds: number[] = [];
  const initialIdx = machine.initial !== null ? index.get(machine.initial) : undefined;
  if (initialIdx !== undefined) seeds.push(initialIdx);
  else for (let v = 0; v < n; v++) if (!incoming[v]) seeds.push(v);
  if (seeds.length === 0 && n > 0) seeds.push(0);

  const bfs = (from: readonly number[], start: number): number => {
    let depth = start;
    let frontier = from.filter((v) => col[v] < 0);
    for (const v of frontier) col[v] = depth;
    while (frontier.length > 0) {
      const next: number[] = [];
      for (const v of frontier) for (const t of out[v]) if (col[t] < 0) { col[t] = depth + 1; next.push(t); }
      if (next.length === 0) break;
      depth += 1;
      frontier = next;
    }
    return depth;
  };
  let deepest = bfs(seeds, 0);
  // A state no move can reach from a seed is still a state the machine has. It is layered
  // after everything reachable — visibly off to the side, never dropped.
  for (let guard = 0; guard < n; guard++) {
    const orphan = col.findIndex((c) => c < 0);
    if (orphan < 0) break;
    deepest = bfs([orphan], deepest + 1);
  }

  const maxCol = n === 0 ? 0 : Math.max(...col);
  const cols: number[][] = [];
  for (let c = 0; c <= maxCol; c++) cols.push(names.map((_, i) => i).filter((i) => col[i] === c));

  // 2. Barycenter ordering: sort each column by the mean position of everything it touches.
  const neighbours: number[][] = names.map((_, v) => {
    const list = [...out[v]];
    for (let u = 0; u < n; u++) if (out[u].includes(v)) list.push(u);
    return list;
  });
  const pos = names.map(() => 0);
  const reindex = (): void => {
    for (const c of cols) c.forEach((v, i) => (pos[v] = i));
  };
  reindex();
  const bary = (v: number): number => {
    const list = neighbours[v];
    return list.length === 0 ? pos[v] : list.reduce((s, u) => s + pos[u], 0) / list.length;
  };
  for (let sweep = 0; sweep < 4; sweep++) {
    for (const c of cols) {
      c.sort((a, b) => bary(a) - bary(b));
      reindex();
    }
  }

  // 3. Coordinates. A self move loops OVER its own pill, so the headroom is reserved above
  //    THAT pill — per looping pill, not once at the top of the body: a looper further down
  //    a column would otherwise draw its loop straight through the pill above it.
  const loopers = new Set<number>();
  for (const t of machine.transitions) {
    const v = index.get(t.from);
    if (v !== undefined && t.from === t.to) loopers.add(v);
  }
  const x = names.map(() => 0);
  const y = names.map(() => 0);
  const w = names.map((_, v) => stateWidth(names[v]));
  let colX = LC_MARGIN + LC_INITIAL_ARROW;
  for (const c of cols) {
    const colW = c.length === 0 ? 0 : Math.max(...c.map((v) => w[v]));
    let colY = LC_MARGIN;
    for (const v of c) {
      if (loopers.has(v)) colY += LC_SELF_LOOP_H; // the loop's own air, above this pill
      x[v] = colX;
      y[v] = colY;
      colY += STATE_H + LC_ROW_GAP;
    }
    colX += colW + LC_COL_GAP;
  }

  const terminal = new Set(machine.terminal);
  const nodes: LcNode[] = states.map((s, v) => ({
    state: s.name,
    index: v,
    column: col[v],
    x: x[v],
    y: y[v],
    w: w[v],
    h: STATE_H,
    initial: machine.initial !== null && s.name === machine.initial && index.get(s.name) === v,
    terminal: terminal.has(s.name),
    pending: s.pending,
  }));
  const bodyBottom = nodes.reduce((m, node) => Math.max(m, node.y + node.h), LC_MARGIN);

  // 4. Moves. Built mutable so the hop-label pass below can de-conflict positions; returned
  //    as the readonly shape.
  type MutableLcEdge = { -readonly [K in keyof LcEdge]: LcEdge[K] };
  const edges: MutableLcEdge[] = [];
  let busLanes = 0;
  /** How many moves already drawn between this exact pair. TRULY parallel moves (same two
   *  states, different events) share every coordinate, so each later one is bowed and its label
   *  dropped a line — two facts about the business must read as two moves. */
  const drawnBetween = new Map<string, number>();
  for (let i = 0; i < machine.transitions.length; i++) {
    const t = machine.transitions[i];
    const a = index.get(t.from);
    const b = index.get(t.to);
    if (a === undefined || b === undefined) continue; // a move naming no pill is not drawn
    const base = {
      index: i,
      from: t.from,
      to: t.to,
      event: t.event,
      guard: t.guard,
      enforcesRule: t.enforcesRule,
      pending: t.pending,
    };

    if (a === b) {
      // A loop over its own pill, in the headroom reserved for it.
      const cx = x[a] + w[a] / 2;
      const top = y[a];
      edges.push({
        ...base,
        self: true,
        lane: -1,
        d: `M ${cx - 18} ${top} C ${cx - 34} ${top - 36}, ${cx + 34} ${top - 36}, ${cx + 18} ${top}`,
        labelX: cx,
        labelY: top - 30,
        bus: false,
      });
      continue;
    }

    const pair = `${a}>${b}`;
    const parallel = drawnBetween.get(pair) ?? 0;
    drawnBetween.set(pair, parallel + 1);

    const forwardHop = col[b] === col[a] + 1;
    if (forwardHop) {
      const x1 = x[a] + w[a];
      const y1 = y[a] + STATE_H / 2;
      const x2 = x[b];
      const y2 = y[b] + STATE_H / 2;
      const mx = (x1 + x2) / 2;
      // A hop's label starts at the curve's own midpoint. That is USUALLY distinct per move —
      // but a symmetric CROSSING pair (A→D and B→C) shares the exact midpoint, so the pass
      // below the loop de-conflicts hop labels per gap by position rather than trusting it.
      const bow = parallel * 22;
      edges.push({
        ...base,
        self: false,
        lane: -1,
        d: `M ${x1} ${y1} C ${mx} ${y1 + bow}, ${mx} ${y2 + bow}, ${x2} ${y2}`,
        labelX: mx,
        labelY: (y1 + y2) / 2 - 7 + parallel * 14,
        bus: false,
      });
      continue;
    }

    // Everything else rides its own lane below the body: back-moves, moves inside one column,
    // and moves that skip a column. Never over a pill the move does not concern.
    const lane = busLanes++;
    const busY = bodyBottom + 24 + lane * LC_BUS_LANE_H;
    const sameCol = col[a] === col[b];
    // Which SIDE each pill is left from and arrived at (+1 = the right edge, −1 = the left).
    const exitSign = sameCol ? -1 : x[b] > x[a] ? 1 : -1;
    const entrySign = sameCol ? -1 : x[b] > x[a] ? -1 : 1;
    const x1 = exitSign > 0 ? x[a] + w[a] : x[a];
    const y1 = y[a] + STATE_H / 2;
    const x2 = entrySign > 0 ? x[b] + w[b] : x[b];
    const y2 = y[b] + STATE_H / 2;
    const outX = x1 + exitSign * 74;
    const inX = x2 + entrySign * 74;
    edges.push({
      ...base,
      self: false,
      lane,
      d: [
        `M ${x1} ${y1}`,
        `C ${x1 + exitSign * 34} ${y1}, ${x1 + exitSign * 44} ${busY}, ${outX} ${busY}`,
        `L ${inX} ${busY}`,
        `C ${x2 + entrySign * 44} ${busY}, ${x2 + entrySign * 34} ${y2}, ${x2} ${y2}`,
      ].join(' '),
      labelX: (outX + inX) / 2,
      labelY: busY - 5,
      bus: true,
    });
  }

  // De-conflict the hop labels, per gap: two crossing moves share the exact midpoint (and
  // nothing else), so any label that would sit on the one above it moves below it instead.
  // Deterministic, and a no-op wherever the midpoints already differ enough.
  const hopsByGap = new Map<number, MutableLcEdge[]>();
  for (const e of edges) {
    if (e.self || e.bus) continue;
    const gap = col[index.get(e.from)!];
    const list = hopsByGap.get(gap);
    if (list === undefined) hopsByGap.set(gap, [e]);
    else list.push(e);
  }
  for (const list of hopsByGap.values()) {
    list.sort((p, q) => p.labelY - q.labelY);
    let floor = -Infinity;
    for (const e of list) {
      if (e.labelY < floor) e.labelY = floor;
      floor = e.labelY + 14;
    }
  }

  return {
    nodes,
    edges,
    width: colX - LC_COL_GAP + LC_MARGIN,
    height: bodyBottom + (busLanes > 0 ? busLanes * LC_BUS_LANE_H + 30 : 0) + LC_MARGIN,
    bodyBottom,
    busLanes,
    columns: cols.length,
  };
}
